import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { EmdChip, EmdFilter, buildChips, chipsToFilter } from './emd-mock-data';

/** Inclusive ISO date bounds from the Time Range badge; either side optional. */
export interface EmdDateRange {
  from?: string;
  to?: string;
}

export type EmdTab = 'Summary' | 'Details' | 'Inventory';

/** Every tab, for callers that apply a change dashboard-wide. */
export const EMD_TABS: EmdTab[] = ['Summary', 'Details', 'Inventory'];

/**
 * Badge filter state for the EMD dashboard.
 *
 * Each tab keeps its own chip row — the tabs cut on different dimensions, and
 * sharing one row would leave a badge applied on a tab that has no such column.
 * The derived `EmdFilter` is what the tabs actually feed to derive*(); it drops
 * select-all dimensions so an untouched badge costs nothing.
 *
 * Distinct from EmdSelectionService: badges *cut* the data, while clicking a
 * visual highlights within it. Both are live at once, which is why they are
 * separate services rather than one merged selection.
 */
@Injectable()
export class EmdFilterService {
  private readonly chipsByTab = new Map<EmdTab, EmdChip[]>();
  private readonly filters = new Map<EmdTab, EmdFilter>();
  /** Confirmed trend-bar period selection (week ISO dates), per tab. Lives
   *  beside the chips because no badge exists for it; Reset clears it. */
  private readonly periodCut = new Map<EmdTab, string[]>();
  /** Time Range badge bounds, per tab like the chips. */
  private readonly dateRanges = new Map<EmdTab, EmdDateRange>();
  private readonly state$ = new BehaviorSubject<EmdTab | null>(null);

  /** Emits the tab whose badges changed, so only that tab re-derives. */
  readonly changes = this.state$.asObservable();

  /** Chips for a tab, built on first use and kept thereafter. */
  chips(tab: EmdTab): EmdChip[] {
    let chips = this.chipsByTab.get(tab);
    if (!chips) {
      chips = buildChips(tab);
      this.chipsByTab.set(tab, chips);
    }
    return chips;
  }

  /** Applied cuts for a tab; `{}` when every badge is at select-all. */
  filter(tab: EmdTab): EmdFilter {
    let out: EmdFilter = this.filters.get(tab) ?? {};
    const periods = this.periodCut.get(tab);
    if (periods?.length) { out = { ...out, periods }; }
    const range = this.dateRanges.get(tab);
    if (range) {
      out = { ...out };
      if (range.from) { out.fromDate = range.from; }
      if (range.to) { out.toDate = range.to; }
    }
    return out;
  }

  /** Apply (or clear, with an empty list) the confirmed period selection. */
  setPeriods(tab: EmdTab, weeks: string[]): void {
    if (weeks.length) { this.periodCut.set(tab, [...weeks]); }
    else { this.periodCut.delete(tab); }
    this.state$.next(tab);
  }

  /** Time Range badge bounds; an empty range clears the cut. */
  setDateRange(tab: EmdTab, range: EmdDateRange): void {
    if (range.from || range.to) { this.dateRanges.set(tab, { ...range }); }
    else { this.dateRanges.delete(tab); }
    this.state$.next(tab);
  }

  /**
   * The tab's Time Range bounds. The "no cut" case returns a shared frozen
   * object rather than a fresh `{}`: this feeds `[dateRange]` on the filter bar
   * and panel, so a new instance per read would look like a changed input on
   * every change-detection pass (repeated `ngOnChanges`, NG0100 in dev mode).
   * Callers never mutate the result — `setDateRange` stores a copy.
   */
  dateRange(tab: EmdTab): EmdDateRange {
    return this.dateRanges.get(tab) ?? EmdFilterService.NO_RANGE;
  }

  private static readonly NO_RANGE: EmdDateRange = Object.freeze({});

  /**
   * Recompute after the filter bar has written a chip's `selected` back.
   * The bar mutates the chip object in place, so there is nothing to pass in.
   */
  apply(tab: EmdTab): void {
    this.filters.set(tab, chipsToFilter(this.chips(tab)));
    this.state$.next(tab);
  }

  /** Restore every badge on a tab to select-all and drop the period cut. */
  reset(tab: EmdTab): void {
    for (const chip of this.chips(tab)) {
      chip.selected = chip.multi ? [...chip.options] : [chip.selected[0] ?? chip.options[0]];
    }
    this.filters.delete(tab);
    this.periodCut.delete(tab);
    this.dateRanges.delete(tab);
    this.state$.next(tab);
  }

  /** True when any badge on the tab is narrowed or a period cut is applied. */
  hasAny(tab: EmdTab): boolean {
    return Object.keys(this.filter(tab)).length > 0;
  }
}
